import BreakdownCard, {
  BreakdownRow,
} from "@/components/molecules/cards/dashboard/BreakdownCard";
import DashboardDateRangeFilter from "@/components/molecules/cards/dashboard/DashboardDateRangeFilter";
import {
  defaultDashboardEndDate,
  defaultDashboardStartDate,
} from "@/helpers/DashboardDateHelpers";
import {
  DossierOrganisationStatRow,
  DossierStatusStatBlock,
} from "@/interfaces/DashboardDossierStats";
import { useGetDashboardDossierStatsQuery } from "@/services/admin/dashboardDossierStats";
import { FC, useState } from "react";
import { Col, Row } from "react-bootstrap";

const statusRows = (block?: DossierStatusStatBlock): BreakdownRow[] =>
  (block?.rows ?? []).map((row) => ({
    key: row.statusId,
    label: row.statusName,
    color: row.backgroundColor,
    amount: row.total,
    count: row.count,
  }));

const organisationRows = (
  rows: DossierOrganisationStatRow[] | undefined,
  metric: "askingPrice" | "agreementAmount",
): BreakdownRow[] =>
  (rows ?? []).map((row) => ({
    key: row.organisationId,
    label: row.organisationName,
    color: row.backgroundColor,
    amount: row[metric],
    count: row.count,
  }));

const GeneralDossierStatsSection: FC = () => {
  const [startDate, setStartDate] = useState<string>(defaultDashboardStartDate);
  const [endDate, setEndDate] = useState<string>(defaultDashboardEndDate);

  const { data, isFetching } = useGetDashboardDossierStatsQuery({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  return (
    <>
      <p className="text-muted mb-2 fs--1">
        Montants des dossiers par Statut de dossier et par Organisme.
      </p>
      <DashboardDateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
      />

      <Row className="g-3 mb-3">
        <Col md={6} xl={3}>
          <BreakdownCard
            title="Montant demandé"
            description="Par Statut de dossier"
            metric="amount"
            rows={statusRows(data?.askingPrice)}
            grandAmount={data?.askingPrice.grandTotal}
            grandCount={data?.askingPrice.grandCount ?? 0}
            isLoading={isFetching}
          />
        </Col>
        <Col md={6} xl={3}>
          <BreakdownCard
            title="Montant accordé"
            description="Par Statut de dossier"
            metric="amount"
            rows={statusRows(data?.agreementAmount)}
            grandAmount={data?.agreementAmount.grandTotal}
            grandCount={data?.agreementAmount.grandCount ?? 0}
            isLoading={isFetching}
          />
        </Col>
        <Col md={6} xl={3}>
          <BreakdownCard
            title="Montant facturé"
            description="Par Statut de dossier"
            metric="amount"
            rows={statusRows(data?.invoiceAmount)}
            grandAmount={data?.invoiceAmount.grandTotal}
            grandCount={data?.invoiceAmount.grandCount ?? 0}
            isLoading={isFetching}
          />
        </Col>
        <Col md={6} xl={3}>
          <BreakdownCard
            title="Montant demandé par Organisme"
            description="Groupé par Organisation (id) — les doublons de nom ne sont pas fusionnés"
            metric="amount"
            rows={organisationRows(data?.byOrganisation.rows, "askingPrice")}
            grandAmount={data?.byOrganisation.grandAskingPrice}
            grandCount={data?.byOrganisation.grandCount ?? 0}
            isLoading={isFetching}
          />
        </Col>
      </Row>

      <Row className="g-3">
        <Col md={6}>
          <BreakdownCard
            title="Montant accordé par Organisme"
            metric="amount"
            rows={organisationRows(
              data?.byOrganisation.rows,
              "agreementAmount",
            )}
            grandAmount={data?.byOrganisation.grandAgreementAmount}
            grandCount={data?.byOrganisation.grandCount ?? 0}
            isLoading={isFetching}
          />
        </Col>
      </Row>
    </>
  );
};

export default GeneralDossierStatsSection;
